import { DEFAULT_PROMPT } from '../constants';
import { EngineType, FakeImage } from '../types';
import { PLACEHOLDER_IMAGE_PATH } from './ImageStuff/GeneratedImage';

import { v4 as uuidv4 } from 'uuid';
import { getEngineUsingModelId } from '../utils/engineHelpers';

const MAX_SEED = 4294967295;

export const getRandomSeed = () => {
  return Math.floor(Math.random() * MAX_SEED);
};

/**
 * Generates a random track id using uuidv4
 */
export const generateTrackId = () => {
  const trackId = uuidv4();
  return trackId;
};

export const waitSeconds = (seconds: number) =>
  new Promise((resolve) => setTimeout(resolve, seconds * 1000));

export const getFailedImage = (): FakeImage => {
  return {
    imageUrl: PLACEHOLDER_IMAGE_PATH,
    trackId: 'failed',
    guidanceScale: 7.5,
    engine: {
      name: 'Realistic women 1',
      modelId: 'realistic-vision-v13',
    },
    negativePrompt: '',
    prompt: '',
    seed: 1,
    height: 512,
    width: 512,
  };
};

export const cleanUpPrompt = (prompt: string): string => {
  // Define an array of common prefixes to remove
  const prefixes = [
    /^create\s+(an|a)\s+(image|picture)\s+of\s+/i,
    /^generate\s+(an|a)\s+(image|picture)\s+of\s+/i,
    /^make\s+(an|a)\s+(image|picture)\s+of\s+/i,
    /^draw\s+(a|an|the)\s+/i,
    /^sketch\s+(a|an|the)\s+/i,
    /^paint\s+(a|an|the)\s+/i,
    /^illustrate\s+(a|an|the)\s+/i,
    /^create\s+(a|an|the)\s+/i,
    /^generate\s+(a|an|the)\s+/i,
    /^make\s+(a|an|the)\s+/i,
    // Add more prefixes as needed
  ];

  let cleanedPrompt = prompt.trim();

  // Iterate through each prefix and remove it if it matches
  for (const prefix of prefixes) {
    if (prefix.test(cleanedPrompt)) {
      cleanedPrompt = cleanedPrompt.replace(prefix, '');
      break; // Remove only the first matching prefix
    }
  }

  // remove any trailing punctuation
  cleanedPrompt = cleanedPrompt.replace(/[.,!?]+$/, '').trim();

  return cleanedPrompt;
};

/*
Remove string from prompt
Filters out the words that are in 
*/
export const getNewNegativePrompt = (negativePrompt: string): string => {
  const stringToRemove = '(underage:1.4), (child:1.3), ';
  return negativePrompt.replace(stringToRemove, '').trim();
};

export {};

export const transformPrompt = (prompt: string) => {
  if (prompt === '') {
    return DEFAULT_PROMPT;
  }
  const wordsToReplace = ['cheeleader'];
  const newPrompt = wordsToReplace.reduce((acc, word) => {
    return acc.replace(word, 'cheerleader');
  }, prompt);

  const transformedPrompt = newPrompt
    .replace('cowwoman', 'cowgirl')
    .replace('catwoman', 'catgirl')
    .replace('1woman', '1girl')
    .replace('2woman', '2girl')
    .replace('3woman', '3girl')
    .replace('womanfriend', 'girlfriend')
    .replace('cowwoman', 'cowgirl')
    .replace('elasticwoman', 'elasticgirl')
    .replace('superwoman', 'supergirl')
    .replace('enfant', '')
    .replace('bambino', '')
    .replace('girls', 'women')
    .replace('girl', 'woman');
  return transformedPrompt;
};

const creditsPerGeneration: { [key: string]: number } = {
  normal: 0,
  fast: 1,
  turbo: 3,
};

/**
 * Note: if changing this, remember to change in functions/helpers as well!
 * 
 * Calculates the number of credits required to generate images based on speed mode, quantity, and engine type.
 * Credits are based on speed mode and quantity; 'normal' mode charges only for 2+ images, while 'fast' and 'turbo' modes charge per image. Gold engines incur additional 1 credit per image.
 *
 * @param {string} speedMode - The mode of speed ('normal', 'fast', 'turbo').
 * @param {number} [amountToGenerate=1] - The number of images to generate. Defaults to 1.
 * @param {EngineType} [engine] - Optional engine specification, including if it's a gold type engine.
 * @returns {number} - The total number of credits required for the operation.
 * 
 * @example
 * // Returns 0 for 1 image in normal mode
 * getCreditsRequired('normal', 1);
 *
 * @example
 * // Returns 5 for 2 images in turbo mode with a gold engine
 * getCreditsRequired('turbo', 2, { name: 'GoldEngine', isGoldOnly: true });

 */
export const getCreditsRequired = (
  speedMode: string,
  amountToGenerate: number = 1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  engine?: EngineType,
): number => {
  let creditsToGenerate = 0;

  if (engine?.modelId === 'superv1mini' && speedMode !== 'adTurbo') {
    return 3;
  }

  if (engine?.modelId === 'superv1') {
    return 5;
  }

  if (speedMode === "adTurbo") {
    return 0;
  }

  // Calculate base credits based on speedMode and amountToGenerate
  if (speedMode === 'normal') {
    creditsToGenerate = amountToGenerate > 1 ? amountToGenerate - 1 : 0;
  } else {
    creditsToGenerate = creditsPerGeneration[speedMode] * amountToGenerate;
  }

  if (!engine) {
    return 5;
  }

  const isGoldOnlyEngine = getEngineUsingModelId(engine.modelId)?.isGoldOnly ?? false;
  if (isGoldOnlyEngine) {
    creditsToGenerate += amountToGenerate * 2;
  }

  return creditsToGenerate;
};

export const isStandalone = () =>
  (window.navigator as any).standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches;

const KEY_FOR_ACCEPTED_TERMS = 'acceptedTerms250408';
export const getHasAcceptedTerms = () => {
  const hasAcceptedTerms = localStorage.getItem(KEY_FOR_ACCEPTED_TERMS);
  if (!hasAcceptedTerms) {
    return false;
  }
  return true;
};

export const setHasAcceptedTerms = () => {
  localStorage.setItem(KEY_FOR_ACCEPTED_TERMS, 'true');
};
